/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */

const getRedirectUri = () => {
    const prodRedirectUri = 'https://arkognize.com/redirect';
    if (typeof window === 'undefined') {
        return prodRedirectUri;
    }

    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocalhost ? `${window.location.origin}/redirect` : prodRedirectUri;
};

export const msalConfig = {
    auth: {
        clientId: '1c37c527-37b5-452d-b266-711ceddede20', // This is the ONLY mandatory field that you need to supply.
        authority: 'https://bimiq.ciamlogin.com/', // Replace the placeholder with your tenant subdomain 
        redirectUri: getRedirectUri(), // Uses localhost in development, prod site in production.
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        // Invite links must return to their exact route after Azure External ID
        // completes a redirect-based sign-in.
        navigateToLoginRequestUrl: true,
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [],
};

/**
 * Add the delegated scope(s) you exposed on your API app registration.
 *
 * By default this sample uses the common scope name "access_as_user".
 * If your API exposes a different scope name (e.g. "user_impersonation"),
 * set REACT_APP_API_SCOPE in your environment or update the default below.
 */
export const apiClientId = '70d0ee9f-f6d5-4012-8fa8-2abb2db5c83f';
export const apiScopeReadRules = `api://${apiClientId}/rules.read`;
export const apiScopeWriteRules = `api://${apiClientId}/rules.write`;

export const apiRequest = {
    scopes: [apiScopeReadRules, apiScopeWriteRules],
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
// export const silentRequest = {
//     scopes: ["openid", "profile"],
//     loginHint: "example@domain.net"
// };
